import React from 'react';

import { Switch } from 'antd';
import { isMobileOnly } from 'react-device-detect';

import TableRowButtons from '@/components/Common/TableRowButtons';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';

import { createTableActionsColumn } from './tableActionColumn';

/**
 * Builds standard configuration list table columns.
 *
 * @param {Object} options
 * @param {Array<{ key: string, titleId?: string, highlight?: boolean, width?: number, align?: string, sorter?: boolean }>} [options.fields]
 * @param {boolean} [options.withActiveSwitch]
 * @param {Function} [options.onActivate] — (id, isActive) => void
 * @param {{ onDetail: Function, onEdit: Function, onRemove: Function }} [options.rowActions] — each receives the row record
 * @param {string} [options.searchCriteria]
 * @param {Function} options.formatMessage
 * @param {boolean} [options.isMobileView]
 */
export function buildConfigColumns({
  fields = [],
  withActiveSwitch = false,
  onActivate,
  activatingItemIds = {},
  rowActions,
  searchCriteria,
  formatMessage,
  isMobileView = isMobileOnly,
}) {
  const columns = [];

  if (withActiveSwitch && onActivate) {
    columns.push({
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      width: 120,
      sorter: true,
      render: (isActive, item) => (
        <Switch
          checked={isActive}
          loading={!!activatingItemIds[item?.id]}
          onChange={(active) => onActivate(item?.id, active)}
        />
      ),
    });
  }

  fields.forEach(({ key, titleId, highlight, width, align, sorter = true }) => {
    columns.push({
      key,
      title: formatMessage({ id: titleId || `global.${key}` }),
      dataIndex: key,
      sorter,
      ...(width != null && { width }),
      ...(align && { align }),
      ...(highlight && { render: createHighlightRender(searchCriteria) }),
    });
  });

  if (rowActions) {
    const { onDetail, onEdit, onRemove } = rowActions;
    columns.push(
      createTableActionsColumn({
        render: (_, record) => (
          <TableRowButtons
            onDetail={() => onDetail(record)}
            onEdit={() => onEdit(record)}
            onRemove={() => onRemove(record)}
            formatMessage={formatMessage}
            isMobileView={isMobileView}
          />
        ),
      }),
    );
  }

  return columns;
}

/**
 * Preset for Tier-1 CRUD modules using CoreQueries NameDescriptionIsActive.
 */
export function buildNameDescriptionIsActiveColumns({
  formatMessage,
  searchCriteria,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  activate,
  activatingItemIds,
  isMobileView,
}) {
  return buildConfigColumns({
    withActiveSwitch: true,
    onActivate: activate,
    activatingItemIds,
    fields: [
      { key: 'name', titleId: 'global.name', highlight: true },
      { key: 'description', titleId: 'global.description', highlight: true },
    ],
    rowActions: {
      onDetail: (record) => openDrawerCreateOrEdit(record.id, true, record),
      onEdit: (record) => openDrawerCreateOrEdit(record.id, false, record),
      onRemove: (record) => deleteItemConfirm(record.id),
    },
    searchCriteria,
    formatMessage,
    isMobileView,
  });
}
